import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { useState } from "react";
import '../Styles/CheckoutForm.scss';

// Collects card details, produces a ConfirmationToken, and hands it to the server.
//
// It never sees an amount. The server prices the cart, re-checks spots and confirms the charge in
// a single request, so the window in which a week can fill between the check and the charge is
// milliseconds rather than however long someone sits on this form. Card details go straight to
// Stripe — only the token id reaches our server.
//
// The 3DS step lives here because stripe.handleNextAction() needs the Stripe instance, which only
// exists inside <Elements>.
//
//   pay(confirmationTokenId)  posts to /cart/pay and returns the server's result
//   onSettled(result)         payment succeeded; the parent finalizes and navigates
function CartCheckoutForm({ pay, onSettled, onProcessingChange, submitLabel = 'Pay Now' }) {
   const stripe = useStripe();
   const elements = useElements();
   const [loading, setLoading] = useState(false);
   const [errorMessage, setErrorMessage] = useState(null);

   const stop = (message) => {
      setErrorMessage(message);
      setLoading(false);
      onProcessingChange?.(false);
   };

   const handleSubmit = async (event) => {
      event.preventDefault();
      if (!stripe || !elements) {
         return;
      }

      setLoading(true);
      setErrorMessage(null);
      onProcessingChange?.(true);

      // Runs the Element's own validation and gathers the details. Required before creating a
      // token in deferred-intent mode.
      const { error: submitError } = await elements.submit();
      if (submitError) {
         return stop(submitError.message);
      }

      const { error: tokenError, confirmationToken } = await stripe.createConfirmationToken({ elements });
      if (tokenError) {
         return stop(tokenError.message);
      }

      const result = await pay(confirmationToken.id);

      if (!result || result.error) {
         return stop(result?.error ?? 'Could not complete your payment.');
      }

      // The card needs authentication. The money has not moved yet.
      if (result.status === 'requires_action') {
         const { error: actionError } = await stripe.handleNextAction({ clientSecret: result.clientSecret });
         if (actionError) {
            return stop(actionError.message);
         }
         // Authenticated and charged, but nothing is recorded yet — the parent calls /cart/finalize.
         return onSettled({ ...result, status: 'completed_after_action' });
      }

      onSettled(result);
   };

   return (
      <form onSubmit={handleSubmit}>
         <PaymentElement />
         {errorMessage &&
            <div style={{ color: "red", marginTop: "10px", fontFamily: "Bebas Neue" }}>{errorMessage}</div>
         }
         <div className="paymentSubmitButton">
            <button type="submit" disabled={!stripe || loading}>
               {loading && <span className="paymentSpinner" aria-hidden="true"></span>}
               {loading ? "Processing..." : submitLabel}
            </button>
         </div>
         {loading &&
            <div className="paymentProcessingNotice">
               Please do not refresh or close this page until your payment is complete and you are redirected.
            </div>
         }
      </form>
   );
}

export default CartCheckoutForm;
